/**
 * Splits Knock-rendered markdown HTML into title + remaining body.
 *
 * Contract (Knock owns copy; FE only presents):
 * 1. Title = plain text of the first `<p>…</p>`, or leading text before the first
 *    block break when there is no paragraph wrapper.
 * 2. Body = remaining HTML after that first paragraph (may be null/empty).
 * 3. A single paragraph → title only, body = null.
 * 4. Display title/body must not come from `item.data` field names.
 */

export type SplitNotificationBodyHtml = {
  /** Plain-text title (tags stripped). Null when the HTML has no usable text. */
  title: string | null;
  /** Remaining HTML after the title paragraph. Null when empty or single-paragraph. */
  bodyHtml: string | null;
};

const FIRST_PARAGRAPH_RE = /<p\b[^>]*>[\s\S]*?<\/p>/i;

/**
 * Strips HTML tags for plain-text display (feed/toast titles).
 */
export function plainTextFromHtml(html: string): string {
  return html.replace(/<[^>]+>/g, '').trim();
}

/**
 * Parses Knock `markdown_body` rendered HTML into title + body remainder.
 */
export function splitNotificationBodyHtml(html: string): SplitNotificationBodyHtml {
  const trimmed = html.trim();
  if (!trimmed) {
    return { title: null, bodyHtml: null };
  }

  const match = FIRST_PARAGRAPH_RE.exec(trimmed);
  if (!match) {
    const title = plainTextFromHtml(trimmed);
    return { title: title || null, bodyHtml: null };
  }

  const leading = trimmed.slice(0, match.index).trim();
  const firstParagraph = match[0];
  const afterFirst = trimmed.slice(match.index + firstParagraph.length).trim();

  // Leading text before the first <p> counts as the title; everything from the
  // first <p> onward is body HTML.
  if (leading) {
    const title = plainTextFromHtml(leading);
    const bodyHtml = `${firstParagraph}${afterFirst ? afterFirst : ''}`.trim() || null;
    return { title: title || null, bodyHtml };
  }

  const title = plainTextFromHtml(firstParagraph);
  return {
    title: title || null,
    bodyHtml: afterFirst || null,
  };
}
